// ============================================================================
// WALK-IN MODAL — Front Office "desk sheet" (July 2026 redesign)
// ============================================================================
// One screen for the highest-frequency desk operation: capture the guest,
// choose the RATE PACKAGE (category + plan — the commercial fact), allocate
// physical rooms against it, take the advance, and complete booking + check-in
// in a single action.
//
// Commercial and allocation are separate on purpose (CRS-CheckIn-GRC-Redesign
// §12): picking a room used to BE the pricing decision, which meant there was
// no rate selection, no meal plan, and no way to see a price before tapping a
// chip. Now the package prices the stay and the chips only say where the guest
// physically sleeps — a chip in another category is an upgrade, hosted there
// but charged as booked.
//
// Orchestrates the EXISTING endpoints (no new backend surface):
//   1. POST /Booking            (source Walk-In; server decides the status)
//   2. POST /Payment            (advance, when captured — may auto-CONFIRM)
//   3. POST /CheckIn/{id}       (picked rooms + registration, when CONFIRMED)
// If step 3 ever fails the booking is CONFIRMED and sits in today's Arrivals
// queue — the normal flow is the graceful fallback, never a dead end.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { availabilityService } from 'api/services/availabilityService';
import { bookingService } from 'api/services/bookingService';
import { checkInService } from 'api/services/checkInService';
import { configService } from 'api/services/configService';
import { paymentService } from 'api/services/paymentService';
import { useAdvancePolicy, usePricingModel } from 'hooks/api/useConfig';
import { calcLineByModel, BOOKING_SOURCE } from 'components/modals/modules/booking-module/booking.utils';
import type { ExtraChargeLine } from 'components/modals/modules/booking-module/booking.utils';
import CheckInGuestsPanel, {
  buildGuestsPayload,
  clearGuestRowErrors,
  createGuestRow,
  validateGuestRows,
} from 'components/modals/modules/booking-module/CheckInGuestsPanel';
import type { GuestRow } from 'components/modals/modules/booking-module/CheckInGuestsPanel';
import SectionedModalShell, {
  ShellBlockers,
  ShellChip,
} from 'components/modals/common/SectionedModalShell';
import type { ShellBlocker, ShellSection } from 'components/modals/common/SectionedModalShell';
import SourceChannelSection from 'components/modals/modules/booking-module/sections/SourceChannelSection';
import WalkInBillingSection from './WalkInBillingSection';
import type { WalkInCommercial } from './WalkInBillingSection';
import type { SourceData } from 'components/modals/modules/booking-module/sections/SourceChannelSection';
import type { AvailabilityResponse, CategoryAvailability } from 'api/types/availability.types';
import type { AvailableRoom, QuickCheckInRequest } from 'api/types/checkin.types';
import type { CreateBookingRequest } from 'api/types';
import { PAYMENT_MODES } from 'api/types/payment.types';

interface WalkInModalProps {
  isOpen: boolean;
  propertyId: string;
  onClose: () => void;
  /** Fired after ANY booking got created (regardless of check-in outcome) so the workspace refreshes. */
  onCompleted: () => void;
  /**
   * Same gate as room-category change (decision: one permission for "may alter
   * what was quoted", not a new one needing its own grants and seeds).
   */
  canEditRate?: boolean;
  /** Enables minimize-to-strip; the strip lives in the workspace header. */
  tabId?: string;
  moduleName?: string;
  /** Reopens this modal when the minimized entry is restored from the strip. */
  onRestoreWalkIn?: () => void;
  /**
   * Parent booking id when this walk-in adds EXTRA rooms to an in-house
   * booking (extra rooms = a new sale at today's rate, never a mutation of
   * the original contract). Persisted as related_booking_id — the group stays
   * visible as one party and the future combined invoice groups by it.
   */
  relatedBookingId?: string;
  /** Parent booking number, display only. */
  relatedBookingNumber?: string;
}

const toDateInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};
const nowTime = (): string => {
  const d = new Date();
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(11, 16);
};
/** date string ± n days → date string. */
const addDays = (date: string, n: number): string => {
  const d = new Date(`${date}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toDateInput(d);
};
/** Whole nights between two date strings (half-open convention). */
const diffNights = (from: string, to: string): number =>
  Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);

const EMPTY_SOURCE: SourceData = {
  bookingSource: BOOKING_SOURCE.WALK_IN,
  agentId: '', agentName: '',
  corporateId: '', corporateName: '',
  otaId: '', otaName: '', otaBookingRef: '',
  settlementMode: 'PARTY',
};
const fmtMoney = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface SelectedRoom {
  roomId: string;
  roomNumber: string;
  roomCategoryId: string;
  housekeepingStatusCode?: string;
}

const WalkInModal: React.FC<WalkInModalProps> = ({
  isOpen, propertyId, onClose, onCompleted, canEditRate = false,
  tabId, moduleName, onRestoreWalkIn, relatedBookingId, relatedBookingNumber,
}) => {
  // ── Guests (shared D3 panel — row 0 is the primary) ──────────────────────
  const [guestRows, setGuestRows] = useState<GuestRow[]>([]);
  const guestKeyCounter = useRef(0);
  // Fallback gender split — visible only while the named adult rows don't all
  // carry a gender (same rule as Quick Check-In).
  const [numMale, setNumMale] = useState('');
  const [numFemale, setNumFemale] = useState('0');

  // ── Source & Channel ─────────────────────────────────────────────────────
  const [sourceData, setSourceData] = useState<SourceData>(EMPTY_SOURCE);
  /** TRUE once the operator picks a payment mode — stops the OTA→Online default. */
  const modeTouchedRef = useRef(false);

  // ── Stay (dates are real fields now; nights stays as a synced stepper) ───
  const [checkInDate, setCheckInDate] = useState<string>('');
  const [checkInTime, setCheckInTime] = useState<string>('');
  const [checkOutDate, setCheckOutDate] = useState<string>('');
  /** Backdated entry window (backdated_booking_max_days) — 0 = today only. */
  const [backdatedMaxDays, setBackdatedMaxDays] = useState<number>(0);
  /** Infants exist only on the check-in record; adults/children live on the package. */
  const [infants, setInfants] = useState('0');

  // ── Rooms (room-first) ───────────────────────────────────────────────────
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);
  const [roomsByCategory, setRoomsByCategory] = useState<Record<string, AvailableRoom[]>>({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState<SelectedRoom[]>([]);
  /**
   * The commercial fact: package + rooms + pax + agreed rate. ONE source of
   * truth for the money — allocation cannot move it, which is what makes an
   * upgrade at the desk free of pricing side-effects.
   */
  const [commercial, setCommercial] = useState<WalkInCommercial | null>(null);
  /** Active nav section (sectioned shell, shared with Quick Check-In). Stay &
   *  Source is the landing section — the dates drive availability, which
   *  everything downstream (package, rooms) depends on. */
  const [activeSection, setActiveSection] = useState<string>('stay');

  // ── Advance ──────────────────────────────────────────────────────────────
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceMode, setAdvanceMode] = useState('Cash');
  const [advanceRef, setAdvanceRef] = useState('');
  const advanceTouchedRef = useRef(false);

  // ── Policies + notes ─────────────────────────────────────────────────────
  const [requireIdDocument, setRequireIdDocument] = useState(false);
  const [requireAllGuestDetails, setRequireAllGuestDetails] = useState(false);
  const [notes, setNotes] = useState('');

  // ── GRC (CRS-CheckIn-GRC-Redesign.md) ────────────────────────────────────
  // Mirrors Quick Check-In so the desk learns ONE registration flow. The number
  // is generated server-side during the check-in leg of the walk-in; the field
  // exists only so a property running a pre-printed GRC book can key the
  // physical card's number instead.
  const [grcNumber, setGrcNumber] = useState('');
  const [grcNumberEditing, setGrcNumberEditing] = useState(false);
  const [grcAddress, setGrcAddress] = useState('');
  const [purposeOfVisit, setPurposeOfVisit] = useState('');
  const [nextDestination, setNextDestination] = useState('');

  const { minimize } = useModalMinimize();
  /** TRUE while reopening from the minimized strip — the reset effect must not wipe the parked state. */
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  const { config: advancePolicy } = useAdvancePolicy(isOpen ? propertyId : undefined);
  const { pricingModel } = usePricingModel(isOpen ? propertyId : undefined);

  const today = toDateInput(new Date());
  /** Nights derived from the real dates (half-open); the stepper edits checkOutDate. */
  const nights = useMemo(
    () => (checkInDate && checkOutDate ? Math.max(0, diffNights(checkInDate, checkOutDate)) : 1),
    [checkInDate, checkOutDate],
  );
  const backdated = !!checkInDate && checkInDate < today;
  const minCheckIn = useMemo(() => addDays(today, -backdatedMaxDays), [today, backdatedMaxDays]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // ── Reset on open ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    guestKeyCounter.current = 0;
    setGuestRows([createGuestRow(`guest-${guestKeyCounter.current++}`, { isPrimary: true })]);
    setNumMale(''); setNumFemale('0');
    setSourceData({ ...EMPTY_SOURCE });
    modeTouchedRef.current = false;
    const now = new Date();
    setCheckInDate(toDateInput(now));
    setCheckInTime(nowTime());
    setCheckOutDate(addDays(toDateInput(now), 1));
    setInfants('0');
    setSelectedRooms([]); setCommercial(null); setActiveSection('stay');
    setAdvanceAmount(''); setAdvanceMode('Cash'); setAdvanceRef('');
    advanceTouchedRef.current = false;
    setNotes(''); setFieldErrors({});
    setGrcNumber(''); setGrcNumberEditing(false);
    setGrcAddress(''); setPurposeOfVisit(''); setNextDestination('');
  }, [isOpen]);

  // ── Per-property policies + backdated window (re-fetched every open) ─────
  useEffect(() => {
    if (!isOpen || !propertyId) {
      setRequireIdDocument(false);
      setRequireAllGuestDetails(false);
      setBackdatedMaxDays(0);
      return;
    }
    let cancelled = false;
    const scopes = [{ type: 'PROPERTY', id: propertyId }];
    configService
      .getEffectiveValue('checkin.require_id_document', scopes)
      .then((res) => {
        if (!cancelled) setRequireIdDocument((res.data?.effectiveValue ?? 'false').toLowerCase() === 'true');
      })
      .catch(() => { if (!cancelled) setRequireIdDocument(false); });
    configService
      .getEffectiveValue('checkin.require_all_guest_details', scopes)
      .then((res) => {
        if (!cancelled) setRequireAllGuestDetails((res.data?.effectiveValue ?? 'false').toLowerCase() === 'true');
      })
      .catch(() => { if (!cancelled) setRequireAllGuestDetails(false); });
    configService
      .getEffectiveValue('backdated_booking_max_days', scopes)
      .then((res) => {
        if (cancelled) return;
        const parsed = parseInt(res.data?.effectiveValue ?? '', 10);
        setBackdatedMaxDays(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
      })
      .catch(() => { if (!cancelled) setBackdatedMaxDays(0); });
    return () => { cancelled = true; };
  }, [isOpen, propertyId]);

  // OTA-sourced walk-ins: guest already paid the OTA — default the advance
  // mode to Online (recorded with the OTA ref) unless the operator picked one.
  useEffect(() => {
    if (!isOpen || modeTouchedRef.current) return;
    setAdvanceMode(sourceData.bookingSource === BOOKING_SOURCE.OTA ? 'Online' : 'Cash');
  }, [isOpen, sourceData.bookingSource]);

  // ── Availability + free physical rooms (the chosen stay window drives it) ─
  useEffect(() => {
    if (!isOpen || !propertyId || !checkInDate || !checkOutDate) return;
    if (checkOutDate <= checkInDate) return; // invalid window — validation surfaces it
    let cancelled = false;
    setLoadingRooms(true);
    (async () => {
      try {
        const avail = await availabilityService.checkAvailability({
          propertyId,
          checkInDate,
          checkOutDate,
          numberOfRooms: 1,
          numAdults: 2,
          numChildren: 0,
        });
        if (cancelled) return;
        const cats = avail.data?.categories ?? [];
        setAvailability(avail.data ?? null);
        // Free PHYSICAL rooms per category on the check-in date.
        const roomLists = await Promise.all(
          cats.map((c) => checkInService
            .getAvailableRooms(propertyId, c.roomCategoryId, checkInDate)
            .catch(() => [] as AvailableRoom[])),
        );
        if (cancelled) return;
        const map: Record<string, AvailableRoom[]> = {};
        cats.forEach((c, i) => { map[c.roomCategoryId] = roomLists[i]; });
        setRoomsByCategory(map);
        // Rooms picked earlier may no longer be free after a window change.
        setSelectedRooms((prev) => prev.filter((r) =>
          (map[r.roomCategoryId] ?? []).some((ar) => ar.roomId === r.roomId)));
      } catch {
        if (!cancelled) notificationService.toast.error('Failed to load availability.');
      } finally {
        if (!cancelled) setLoadingRooms(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyId, checkInDate, checkOutDate]);

  // ── Guest-row helpers (shared Guests panel) ──────────────────────────────
  const addGuestRow = useCallback(() => {
    setGuestRows((prev) => [...prev, createGuestRow(`guest-${guestKeyCounter.current++}`, {
      nationality: prev.find((g) => g.isPrimary)?.nationality ?? 'India',
      state: prev.find((g) => g.isPrimary)?.state ?? '',
    })]);
  }, []);

  const removeGuestRow = useCallback((key: string) => {
    setGuestRows((prev) => prev.filter((g) => g.key !== key || g.isPrimary));
    setFieldErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k.endsWith(`-${key}`)) delete next[k]; });
      return next;
    });
  }, []);

  const updateGuestRow = useCallback(
    (key: string, patch: Partial<Omit<GuestRow, 'key' | 'id' | 'isPrimary'>>) => {
      setGuestRows((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));
      setFieldErrors((prev) => clearGuestRowErrors(prev, key, patch));
    },
    [],
  );
  /**
   * Exactly one primary guest, enforced by construction rather than validated
   * after the fact: setting one clears every other, so the invariant cannot be
   * broken by the UI at all.
   */
  const setPrimaryGuest = useCallback((key: string) => {
    setGuestRows((prev) => prev.map((g) => ({ ...g, isPrimary: g.key === key })));
  }, []);


  // ── Selection helpers ────────────────────────────────────────────────────
  const toggleRoom = useCallback((cat: CategoryAvailability, room: AvailableRoom) => {
    clearFieldError('rooms');
    setSelectedRooms((prev) => {
      const exists = prev.some((r) => r.roomId === room.roomId);
      if (exists) return prev.filter((r) => r.roomId !== room.roomId);
      // Allocation follows the package, never the other way round.
      if (!commercial) {
        notificationService.toast.info('Choose a rate package first — rooms are allocated against it.');
        return prev;
      }
      if (prev.length >= commercial.numberOfRooms) {
        notificationService.toast.info(
          `All ${commercial.numberOfRooms} room${commercial.numberOfRooms !== 1 ? 's' : ''} are allocated — release one first, or increase the room count in Rate & Charges.`,
        );
        return prev;
      }
      return [...prev, {
        roomId: room.roomId,
        roomNumber: room.roomNumber,
        roomCategoryId: cat.roomCategoryId,
        housekeepingStatusCode: room.housekeepingStatusCode,
      }];
    });
  }, [commercial, clearFieldError]);

  // ── Pricing — from the COMMERCIAL fact only ──────────────────────────────
  // Allocation is deliberately absent from the money: moving a guest's rooms
  // between categories (an upgrade) cannot change what they pay. The payload
  // lines below carry the allocation split, but every line is booked and rated
  // as the package category.
  const { detailLines, total, belowMin } = useMemo(() => {
    if (!commercial) {
      return { detailLines: [] as ExtraChargeLine[], total: 0, belowMin: false };
    }
    const n = Math.max(1, nights);

    // Allocation groups by the category the room actually sits in. Before any
    // room is picked, price against a single unallocated line so the operator
    // sees the money as soon as the package is chosen.
    const groups: { categoryId: string; rooms: number }[] = [];
    for (const r of selectedRooms) {
      const g = groups.find((x) => x.categoryId === r.roomCategoryId);
      if (g) g.rooms += 1; else groups.push({ categoryId: r.roomCategoryId, rooms: 1 });
    }
    if (groups.length === 0) {
      groups.push({ categoryId: commercial.roomCategoryId, rooms: commercial.numberOfRooms });
    }

    // Pax distributed proportionally to rooms; the last line takes the
    // remainder so nothing is lost to rounding.
    const totalRooms = groups.reduce((a, g) => a + g.rooms, 0);
    let remAdults = commercial.numAdults;
    let remChildren = commercial.numChildren;
    let remRooms = totalRooms;

    const lines: ExtraChargeLine[] = groups.map((g) => {
      const last = remRooms === g.rooms;
      const lineAdults = last ? remAdults
        : Math.min(remAdults, Math.round(commercial.numAdults * (g.rooms / totalRooms)));
      const lineChildren = last ? remChildren
        : Math.min(remChildren, Math.round(commercial.numChildren * (g.rooms / totalRooms)));
      remAdults -= lineAdults; remChildren -= lineChildren; remRooms -= g.rooms;
      return {
        // Booked and rated as the PACKAGE category; hosted where allocated.
        roomCategoryId: commercial.roomCategoryId,
        allocatedRoomCategoryId: g.categoryId !== commercial.roomCategoryId ? g.categoryId : undefined,
        numberOfRooms: g.rooms,
        ratePlanId: commercial.ratePlanId,
        ratePlanDetailId: commercial.ratePlanDetailId,
        mealPlanId: commercial.mealPlanId,
        // RACK on the line, always — the negotiated difference travels as a
        // rate-adjustment row so the standard survives for reporting.
        ratePerRoom: commercial.ratePerRoom,
        extraAdultRate: commercial.extraAdultRate,
        extraChildRate: commercial.extraChildRate,
        mealAdultRate: commercial.mealAdultRate,
        mealChildRate: commercial.mealChildRate,
        numAdults: lineAdults,
        numChildren: lineChildren,
        baseOccupancy: commercial.baseOccupancy,
      };
    });

    const standardSum = lines.reduce(
      (sum, l) => sum + calcLineByModel(pricingModel, l, n).lineTotal, 0);

    // Component-wise deltas, mirroring the booking form's adjustment mirror of
    // BookingAdjustmentCalculator: room per room-night, meal per adult-night,
    // extra per extra-person-night (qty derived from the priced extras so the
    // basis matches whatever the pricing model actually charged).
    let adjustmentSum = 0;
    const addDelta = (agreed: number | undefined, rack: number, qty: number) => {
      if (agreed == null || agreed === rack || qty <= 0) return;
      adjustmentSum += Math.round((agreed - rack) * qty * 100) / 100;
    };
    // Quantities come from the pricing calculator, matching the server's
    // LineCharges.*Units. Previously room/meal were inferred here and extras were
    // derived by dividing the extras money by the ADULT rate — which blends in
    // children priced at extraChildRate, and which for a bundled meal made the
    // discount grow with occupancy while the charge stayed flat.
    const units = lines.reduce(
      (acc, l) => {
        const c = calcLineByModel(pricingModel, l, n);
        return {
          room: acc.room + c.roomUnits,
          meal: acc.meal + c.mealAdultUnits,
          extra: acc.extra + c.extraAdultUnits,
        };
      },
      { room: 0, meal: 0, extra: 0 },
    );
    addDelta(commercial.agreedRatePerRoom, commercial.ratePerRoom, units.room);
    addDelta(commercial.agreedMealAdultRate, commercial.mealAdultRate, units.meal);
    addDelta(commercial.agreedExtraAdultRate, commercial.extraAdultRate, units.extra);

    // Minimum-rate check against the chosen plan's floor.
    const plan = availability?.categories
      .find((c) => c.roomCategoryId === commercial.roomCategoryId)
      ?.ratePlans.find((rp) => rp.ratePlanDetailId === commercial.ratePlanDetailId);
    const effectiveRoomRate = commercial.agreedRatePerRoom ?? commercial.ratePerRoom;
    const isBelowMin = !!plan && plan.minimumRate > 0 && effectiveRoomRate < plan.minimumRate;

    return {
      detailLines: lines,
      total: standardSum + adjustmentSum,
      belowMin: isBelowMin,
    };
  }, [commercial, selectedRooms, nights, pricingModel, availability]);

  // ── Advance required (policy estimate) — prefills the amount field ────────
  const advanceRequired = useMemo(() => {
    const pct = advancePolicy?.advancePercent ?? 0;
    return Math.round((total * pct) / 100);
  }, [total, advancePolicy]);

  useEffect(() => {
    if (!advanceTouchedRef.current) {
      setAdvanceAmount(advanceRequired > 0 ? String(advanceRequired) : '');
    }
  }, [advanceRequired]);

  const dirtySelectedRooms = useMemo(
    () => selectedRooms.filter((r) => r.housekeepingStatusCode === 'ROOM_DIRTY'),
    [selectedRooms]);

  // ── Submit orchestration ─────────────────────────────────────────────────
  const executeWalkIn = useCallback(async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    const primaryRow = guestRows.find((g) => g.isPrimary);
    const primaryName = (primaryRow?.name ?? '').trim();
    const nameParts = primaryName.split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || undefined;
    const advAmt = Math.max(0, Number(advanceAmount) || 0);
    let bookingNumber = '';
    try {
      // 1 — booking (server decides the status: E3a advance rules, approvals)
      const payload: CreateBookingRequest = {
        propertyId,
        bookingSource: sourceData.bookingSource || BOOKING_SOURCE.WALK_IN,
        agentId: sourceData.agentId || undefined,
        corporateId: sourceData.corporateId || undefined,
        otaId: sourceData.otaId || undefined,
        otaName: sourceData.otaName || undefined,
        otaBookingRef: sourceData.otaBookingRef.trim() || undefined,
        settlementMode: sourceData.settlementMode,
        relatedBookingId: relatedBookingId || undefined,
        checkInDate,
        checkOutDate,
        specialNotes: notes.trim() || undefined,
        isVip: false,
        isComplimentary: false,
        detailLines: detailLines.map(({ baseOccupancy, childAgeLimit, chargeableChildAge, infantAgeLimit, ...line }) => line),
        // Negotiated figures are ADJUSTMENT rows against the rack rates saved on
        // every line — never overwrites — one row per changed COMPONENT per
        // line, the same shape the booking form's buildAdjustments emits.
        adjustments: commercial
          ? detailLines.flatMap((_, idx) =>
              ([
                ['ROOM', commercial.agreedRatePerRoom, commercial.ratePerRoom],
                ['MEAL', commercial.agreedMealAdultRate, commercial.mealAdultRate],
                ['EXTRA_PERSON', commercial.agreedExtraAdultRate, commercial.extraAdultRate],
              ] as Array<[string, number | undefined, number]>)
                .filter(([, agreed, rack]) => agreed != null && agreed !== rack)
                .map(([componentCode, agreed]) => ({
                  componentCode,
                  adjustmentType: 'AgreedRate',
                  detailLineIndex: idx,
                  agreedUnitRate: agreed as number,
                  remarks: commercial.rateChangeReason?.trim() || 'Walk-in rate agreed at the desk',
                })))
          : [],
        totalRooms: commercial?.numberOfRooms,
        guests: [{
          guestType: 'Adult',
          firstName,
          lastName,
          isChargeable: true,
          isPrimary: true,
          // Contact lives on the guest row now — one entry point, not two.
          phone: primaryRow?.phone?.trim() || undefined,
          email: primaryRow?.email?.trim() || undefined,
        }],
        addons: [],
        saveAsConfirmed: true,
      };
      const created = await bookingService.create(payload);
      const bookingId: string = created.data?.bookingId || (created.data as any)?.id;
      bookingNumber = created.data?.bookingNumber ?? '';
      let statusCode = (created.data?.bookingStatusCode || '').toUpperCase();
      if (!bookingId) throw new Error('Booking was not created.');

      // 2 — advance (may auto-confirm a TENTATIVE booking)
      if (advAmt > 0) {
        try {
          await paymentService.create({
            bookingId,
            amount: advAmt,
            paymentMode: advanceMode,
            paymentDate: today,
            paymentReference: advanceRef.trim()
              || (sourceData.bookingSource === BOOKING_SOURCE.OTA ? sourceData.otaBookingRef.trim() : '')
              || undefined,
            paymentType: 'Advance',
            remarks: 'Walk-in advance',
            collectionPoint: 'FRONT_OFFICE',
          });
        } catch (payErr: any) {
          notificationService.toast.warning(
            `Booking "${bookingNumber}" created, but the advance failed: ${payErr?.response?.data?.message || payErr?.message || 'error'}. Record it via the booking's Record Advance action.`,
            8000,
          );
        }
      }

      // Fresh read after create(+advance): current status AND the primary
      // guest row's id — the check-in guests payload UPDATES that row (keeps
      // phone/email) instead of replacing it.
      let primaryGuestId: string | undefined;
      try {
        const fresh = await bookingService.getFullBooking(bookingId);
        statusCode = (fresh.data?.booking?.bookingStatusCode || statusCode).toUpperCase();
        primaryGuestId = fresh.data?.guests?.find((g) => g.isPrimary)?.id;
      } catch { /* fall through with the create-time status */ }

      // 3 — check-in (only valid from CONFIRMED)
      if (statusCode === 'CONFIRMED') {
        try {
          const guests = buildGuestsPayload(guestRows).map((g, i) => (
            i === 0 || g.isPrimary ? { ...g, id: g.id ?? primaryGuestId } : g
          ));
          const adultsRows = guestRows.filter((g) => g.name.trim() && g.guestType === 'Adult');
          const gendersComplete = adultsRows.length > 0 && adultsRows.every((g) => !!g.gender);
          const checkinPayload: QuickCheckInRequest = {
            checkInDate,
            checkInTime: checkInTime.length === 5 ? `${checkInTime}:00` : checkInTime,
            checkInNotes: notes.trim() || undefined,
            guests: guests.length > 0 ? guests : undefined,
            numMale: !gendersComplete && numMale.trim() !== '' ? Number(numMale) : undefined,
            numFemale: !gendersComplete && numFemale.trim() !== '' ? Number(numFemale) : undefined,
            // Actual pax on site — for walk-ins booked = actual by definition,
            // and booked lives on the package.
            numAdults: commercial?.numAdults,
            numChildren: commercial?.numChildren,
            numInfants: infants.trim() !== '' ? Number(infants) : undefined,
            // Over-ceiling arrivals need the recorded reason (tables/082) or
            // the check-in call is refused with a 400. Only sent while the pax
            // are actually over — a reason made moot by a correction must not
            // be recorded.
            occupancyOverrideReason: commercial
              && (commercial.numAdults + commercial.numChildren)
                > commercial.maxOccupancy * (commercial.numberOfRooms || 0)
              ? commercial.occupancyOverrideReason?.trim() || undefined
              : undefined,
            // GRC. Only send a number when the operator overrode it for a
            // pre-printed card — an empty string would defeat the server-side
            // series. Same contract as Quick Check-In.
            grcNumber: grcNumber.trim() || undefined,
            grcAddress: grcAddress.trim() || undefined,
            purposeOfVisit: purposeOfVisit.trim() || undefined,
            nextDestination: nextDestination.trim() || undefined,
            rooms: selectedRooms.map((r) => ({ roomId: r.roomId })),
          };
          const checkedIn = await checkInService.checkIn(bookingId, checkinPayload);
          notificationService.toast.success(
            checkedIn.grcNumber
              ? `Walk-in "${bookingNumber}" checked in — Registration Card ${checkedIn.grcNumber}.`
              : `Walk-in "${bookingNumber}" checked in.`,
          );
        } catch (ciErr: any) {
          notificationService.toast.warning(
            `Booking "${bookingNumber}" is CONFIRMED but check-in failed: ${ciErr?.response?.data?.message || ciErr?.message || 'error'}. Complete it from today's Arrivals queue.`,
            8000,
          );
        }
      } else if (statusCode === 'PENDING_APPROVAL') {
        notificationService.toast.info(
          `Walk-in "${bookingNumber}" needs approval (see the Approval Inbox). Check in from Arrivals once approved.`,
          8000,
        );
      } else {
        notificationService.toast.info(
          `Walk-in "${bookingNumber}" saved as ${statusCode || 'TENTATIVE'} — the advance received is below the required amount. Complete the advance to confirm, then check in from Arrivals.`,
          8000,
        );
      }

      onCompleted();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message || 'Walk-in failed.';
      notificationService.toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [guestRows, numMale, numFemale, notes, sourceData, commercial, infants,
      grcNumber, grcAddress, purposeOfVisit, nextDestination,
      advanceAmount, advanceMode, advanceRef, selectedRooms, detailLines, propertyId,
      today, checkInDate, checkInTime, checkOutDate, onCompleted, onClose]);

  const handleSubmit = useCallback(() => {
    const errors: Record<string, string> = {};
    // Stay window.
    if (!checkInDate) errors.checkInDate = 'Check-in date is required.';
    if (!checkInTime) errors.checkInTime = 'Check-in time is required.';
    if (checkInDate && checkInDate > today) {
      errors.checkInDate = 'A future arrival is a reservation — use the full booking form.';
    }
    if (checkInDate && checkInDate < minCheckIn) {
      errors.checkInDate = backdatedMaxDays > 0
        ? `This property allows backdated entry up to ${backdatedMaxDays} day${backdatedMaxDays !== 1 ? 's' : ''} back (${minCheckIn}).`
        : 'This property does not allow backdated entry.';
    }
    if (checkInDate && checkOutDate && checkOutDate <= checkInDate) {
      errors.checkOutDate = 'Check-out must be after check-in.';
    }
    // Guests — shared panel validation.
    Object.assign(errors, validateGuestRows(guestRows, { requireAllGuestDetails, requireIdDocument }));
    // Source conditional requireds (mirrors the booking form).
    if (sourceData.bookingSource === BOOKING_SOURCE.TRAVEL_AGENT && !sourceData.agentId) errors.agentId = 'Select the agent.';
    if (sourceData.bookingSource === BOOKING_SOURCE.CORPORATE && !sourceData.corporateId) errors.corporateId = 'Select the corporate.';
    if (sourceData.bookingSource === BOOKING_SOURCE.OTA) {
      if (!sourceData.otaId) errors.otaId = 'Select the OTA.';
      if (!sourceData.otaBookingRef.trim()) errors.otaBookingRef = 'OTA booking reference is required.';
    }
    // Commercial before allocation — the package is the booking.
    if (!commercial) {
      errors.package = 'Select a rate package.';
    } else {
      if (commercial.numAdults < 1) errors.commercialPax = 'At least one adult.';
      const effRoom = commercial.agreedRatePerRoom ?? commercial.ratePerRoom;
      if (effRoom <= 0) errors.rate = 'The rate must be above zero.';
      const anyConcession =
        (commercial.agreedRatePerRoom != null && commercial.agreedRatePerRoom < commercial.ratePerRoom)
        || (commercial.agreedMealAdultRate != null && commercial.agreedMealAdultRate < commercial.mealAdultRate)
        || (commercial.agreedExtraAdultRate != null && commercial.agreedExtraAdultRate < commercial.extraAdultRate);
      if (anyConcession && !commercial.rateChangeReason?.trim()) {
        errors.rateChangeReason = 'A concession needs a reason — it goes to the approver.';
      }
      // Over the occupancy ceiling (maxOccupancy × rooms) — allowed, but the
      // check-in server requires a recorded reason (tables/082). Caught here
      // so the walk-in doesn't degrade to "check in from Arrivals" on a 400.
      const overOccupancy = (commercial.numAdults + commercial.numChildren)
        > commercial.maxOccupancy * (commercial.numberOfRooms || 0);
      if (overOccupancy && !commercial.occupancyOverrideReason?.trim()) {
        errors.occupancyOverrideReason =
          'Guests exceed the rooms\' maximum occupancy — record a reason to proceed.';
      }
      // Allocation must match the count sold — the same rule the per-pax
      // booking enforces server-side. A short allocation surfaces HERE, not
      // when the guest is standing at the desk tomorrow.
      if (selectedRooms.length !== commercial.numberOfRooms) {
        errors.rooms = selectedRooms.length < commercial.numberOfRooms
          ? `Allocate ${commercial.numberOfRooms - selectedRooms.length} more room${commercial.numberOfRooms - selectedRooms.length !== 1 ? 's' : ''} — ${selectedRooms.length} of ${commercial.numberOfRooms} picked.`
          : `${selectedRooms.length} rooms picked but the package is for ${commercial.numberOfRooms}.`;
      }
    }
    const advAmt = Math.max(0, Number(advanceAmount) || 0);
    if (advAmt > 0 && !advanceMode) errors.advanceMode = 'Pick a payment mode.';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Jump to the first section (nav order) that carries an error, so the
      // operator lands ON the problem instead of hunting the red badge.
      const sectionOf = (k: string): string =>
        ['checkInDate', 'checkInTime', 'checkOutDate', 'agentId', 'corporateId', 'otaId', 'otaBookingRef'].includes(k) ? 'stay'
          : ['package', 'commercialPax', 'rate', 'rateChangeReason', 'occupancyOverrideReason', 'advanceMode'].includes(k) ? 'billing'
          : k === 'rooms' ? 'rooms'
          : 'guests';
      const first = ['stay', 'guests', 'grc', 'billing', 'rooms']
        .find((s) => Object.keys(errors).some((k) => sectionOf(k) === s));
      if (first) setActiveSection(first);
      return;
    }
    setFieldErrors({});

    // Confirm only when something needs a second look — same convention as
    // the Quick Check-In redesign.
    const warnings: string[] = [];
    if (backdated) {
      warnings.push(`Backdated entry — the stay is recorded from ${checkInDate} (${nights} night${nights !== 1 ? 's' : ''}).`);
    }
    if (belowMin && commercial) {
      warnings.push(`Rate below the plan minimum for ${commercial.categoryName} — the booking will go for APPROVAL and check-in completes after it is approved.`);
    }
    if (advanceRequired > 0 && advAmt < advanceRequired) {
      warnings.push(`Advance ₹${fmtMoney(advAmt)} is below the required ₹${fmtMoney(advanceRequired)} — the booking will stay TENTATIVE (no check-in) until the advance is completed.`);
    }
    if (dirtySelectedRooms.length > 0) {
      warnings.push(`Room${dirtySelectedRooms.length !== 1 ? 's' : ''} ${dirtySelectedRooms.map((r) => r.roomNumber).join(', ')} still marked Dirty.`);
    }
    if (warnings.length > 0) {
      setConfirmMessage(warnings.join(' '));
      setConfirmOpen(true);
      return;
    }
    void executeWalkIn();
  }, [guestRows, selectedRooms, commercial, advanceAmount, advanceMode,
      checkInDate, checkInTime, checkOutDate, today, minCheckIn, backdatedMaxDays, backdated, nights,
      sourceData, requireAllGuestDetails,
      requireIdDocument, belowMin, advanceRequired,
      dirtySelectedRooms, executeWalkIn]);

  /**
   * Park the sheet on the strip. Everything the operator typed comes back on
   * restore — the desk minimizes a walk-in precisely because another guest
   * interrupted, and losing the half-entered sheet would be worse than not
   * having the button.
   */
  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const saved = {
      guestRows, numMale, numFemale, sourceData,
      checkInDate, checkInTime, checkOutDate, infants,
      selectedRooms, commercial, activeSection,
      advanceAmount, advanceMode, advanceRef, notes,
      grcNumber, grcAddress, purposeOfVisit, nextDestination,
      guestKeyCounter: guestKeyCounter.current,
      modeTouched: modeTouchedRef.current,
      advanceTouched: advanceTouchedRef.current,
    };
    minimize({
      id: `${tabId}-walkin-modal`,
      title: `Walk-In${saved.guestRows.find((g) => g.isPrimary)?.name.trim() ? ` - ${saved.guestRows.find((g) => g.isPrimary)!.name.trim()}` : ''}`,
      moduleName: moduleName ?? 'Front Office',
      tabId,
      // A sheet with anything on it is worth warning about on tab close.
      isDirty: !!(saved.commercial || saved.selectedRooms.length > 0
        || saved.guestRows.some((g) => g.name.trim())),
      onRestore: () => {
        isRestoreRef.current = true;
        setGuestRows(saved.guestRows);
        setNumMale(saved.numMale); setNumFemale(saved.numFemale);
        setSourceData(saved.sourceData);
        setCheckInDate(saved.checkInDate); setCheckInTime(saved.checkInTime);
        setCheckOutDate(saved.checkOutDate);
        setInfants(saved.infants);
        setSelectedRooms(saved.selectedRooms);
        setCommercial(saved.commercial);
        setActiveSection(saved.activeSection);
        setAdvanceAmount(saved.advanceAmount); setAdvanceMode(saved.advanceMode);
        setAdvanceRef(saved.advanceRef);
        setNotes(saved.notes);
        setGrcNumber(saved.grcNumber); setGrcAddress(saved.grcAddress);
        setPurposeOfVisit(saved.purposeOfVisit); setNextDestination(saved.nextDestination);
        guestKeyCounter.current = saved.guestKeyCounter;
        modeTouchedRef.current = saved.modeTouched;
        advanceTouchedRef.current = saved.advanceTouched;
        onRestoreWalkIn?.();
      },
    });
    onClose();
  }, [tabId, moduleName, guestRows, numMale, numFemale, sourceData,
      checkInDate, checkInTime, checkOutDate, infants, selectedRooms, commercial,
      activeSection, advanceAmount, advanceMode, advanceRef, notes,
      grcNumber, grcAddress, purposeOfVisit, nextDestination,
      minimize, onClose, onRestoreWalkIn]);

  if (!isOpen) return null;

  const advAmtNum = Math.max(0, Number(advanceAmount) || 0);
  // Fallback gender inputs disappear once every named adult row has a gender.
  const namedAdults = guestRows.filter((g) => g.name.trim() && g.guestType === 'Adult');
  const showGenderFallback = !(namedAdults.length > 0 && namedAdults.every((g) => !!g.gender));

  /** GRC completeness — informational only; none of these refuse a walk-in. */
  const grcFilled = [grcAddress, purposeOfVisit, nextDestination]
    .filter((v) => v.trim()).length;

  // ── Per-section validation errors (nav badges + first-error jump) ───────
  const errorSectionOf = (key: string): string =>
    ['checkInDate', 'checkInTime', 'checkOutDate', 'agentId', 'corporateId', 'otaId', 'otaBookingRef'].includes(key) ? 'stay'
      : ['package', 'commercialPax', 'rate', 'rateChangeReason', 'occupancyOverrideReason', 'advanceMode'].includes(key) ? 'billing'
      : key === 'rooms' ? 'rooms'
      : 'guests';
  const errorCounts: Record<string, number> = {};
  Object.keys(fieldErrors).forEach((k) => {
    const s = errorSectionOf(k);
    errorCounts[s] = (errorCounts[s] ?? 0) + 1;
  });

  const hasPrimaryName = guestRows.some((g) => g.isPrimary && g.name.trim());
  const blockerItems: ShellBlocker[] = [
    ...(!commercial ? [{ label: 'Choose a rate package', sectionId: 'billing' }] : []),
    ...(commercial && selectedRooms.length !== commercial.numberOfRooms
      ? [{
          label: `Allocate ${commercial.numberOfRooms} room${commercial.numberOfRooms !== 1 ? 's' : ''} (${selectedRooms.length} picked)`,
          sectionId: 'rooms',
        }]
      : []),
    ...(!hasPrimaryName ? [{ label: 'Primary guest name', sectionId: 'guests' }] : []),
  ];

  // ══════════════════════════════════════════════════════════════════════
  // SECTIONS — same shell as Quick Check-In, one order difference: Rate &
  // Advance sits BEFORE Rooms because allocation follows the package (the
  // commercial fact prices the stay; the chips only say where the guest
  // physically sleeps). Rooms without a package is a refused tap.
  // ══════════════════════════════════════════════════════════════════════

  const sections: ShellSection[] = [
    // ── ① Stay & Source — the window drives availability; the channel
    //      drives attribution and the advance-mode default. ──
    {
      id: 'stay',
      title: 'Stay & Source',
      badge: checkInDate && checkInTime && checkOutDate
        ? (backdated ? 'warn' : 'ok')
        : undefined,
      errorCount: errorCounts['stay'],
      content: (
        <>
          {/* Stay dates — real fields (FO review 2026-08); nights stepper
              stays for the fast 1–2 night case, synced both ways. */}
          <div className="form-grid-3" style={{ marginBottom: '0.5rem' }}>
            <div className="form-group" style={{ marginBottom: 0 }} data-field="checkInDate">
              <label className="form-label">Check-In <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.checkInDate ? ' form-field-error' : ''}`}
                value={checkInDate}
                min={minCheckIn}
                max={today}
                onChange={(e) => {
                  clearFieldError('checkInDate');
                  const v = e.target.value;
                  // Keep the night count — shifting arrival shifts departure.
                  setCheckOutDate((prev) => {
                    const n = checkInDate && prev ? Math.max(1, diffNights(checkInDate, prev)) : 1;
                    return v ? addDays(v, n) : prev;
                  });
                  setCheckInDate(v);
                }}
                disabled={submitting}
              />
              {fieldErrors.checkInDate && <div className="form-error-message">{fieldErrors.checkInDate}</div>}
            </div>
            <div className="form-group" style={{ marginBottom: 0 }} data-field="checkInTime">
              <label className="form-label">Time <span className="text-danger">*</span></label>
              <input
                type="time"
                className={`form-input${fieldErrors.checkInTime ? ' form-field-error' : ''}`}
                value={checkInTime}
                onChange={(e) => { clearFieldError('checkInTime'); setCheckInTime(e.target.value); }}
                disabled={submitting}
              />
              {fieldErrors.checkInTime && <div className="form-error-message">{fieldErrors.checkInTime}</div>}
            </div>
            <div className="form-group" style={{ marginBottom: 0 }} data-field="checkOutDate">
              <label className="form-label">Check-Out <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${fieldErrors.checkOutDate ? ' form-field-error' : ''}`}
                value={checkOutDate}
                min={checkInDate ? addDays(checkInDate, 1) : undefined}
                onChange={(e) => { clearFieldError('checkOutDate'); setCheckOutDate(e.target.value); }}
                disabled={submitting}
              />
              {fieldErrors.checkOutDate && <div className="form-error-message">{fieldErrors.checkOutDate}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.75rem', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            <button type="button" className="btn btn-secondary" style={{ padding: '0.15rem 0.55rem' }}
              onClick={() => { clearFieldError('checkOutDate'); setCheckOutDate((prev) => (checkInDate && prev && diffNights(checkInDate, prev) > 1 ? addDays(prev, -1) : prev)); }}
              disabled={submitting || nights <= 1}>−</button>
            <strong style={{ color: 'var(--text-primary)' }}>{nights} night{nights !== 1 ? 's' : ''}</strong>
            <button type="button" className="btn btn-secondary" style={{ padding: '0.15rem 0.55rem' }}
              onClick={() => { clearFieldError('checkOutDate'); setCheckOutDate((prev) => (prev ? addDays(prev, 1) : prev)); }}
              disabled={submitting || nights >= 30}>+</button>
            {backdated && (
              <span style={{
                padding: '0.05rem 0.5rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
                background: 'rgba(37, 99, 235, 0.1)', color: '#1d4ed8', border: '1px solid rgba(37, 99, 235, 0.3)',
              }}>
                Backdated entry
              </span>
            )}
          </div>

          {/* Source & Channel — walk-ins can arrive via OTA / agent /
              corporate too; attribution + party link, no re-pricing
              (negotiated rates go through the per-category rate field). */}
          <SourceChannelSection
            data={sourceData}
            onChange={(updates) => {
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.agentId; delete next.corporateId; delete next.otaId; delete next.otaBookingRef;
                return next;
              });
              setSourceData((prev) => ({ ...prev, ...updates }));
            }}
            fieldErrors={fieldErrors}
          />
        </>
      ),
    },

    // ── ② Guests & Occupancy — who is on the stay, plus the headcount.
    //      Merged (Aug 2026 redesign): for a walk-in booked pax IS actual, so
    //      occupancy is just the gender fallback + infants. ──
    {
      id: 'guests',
      title: 'Guests & Occupancy',
      badge: {
        done: guestRows.filter((g) => g.name.trim()).length,
        total: Math.max(guestRows.length, 1),
      },
      errorCount: (errorCounts['guests'] ?? 0)
        // The primary-name blocker only counts until a guestName field error
        // (post-submit) covers the same issue — no double badge.
        + (hasPrimaryName || Object.keys(fieldErrors).some((k) => k.startsWith('guestName-')) ? 0 : 1),
      content: (
        <>
          <CheckInGuestsPanel
            guestRows={guestRows}
            onUpdateRow={updateGuestRow}
            onSetPrimary={setPrimaryGuest}
            onAddRow={addGuestRow}
            onRemoveRow={removeGuestRow}
            fieldErrors={fieldErrors}
            submitting={submitting}
            requireAllGuestDetails={requireAllGuestDetails}
          />

          {/* ── Occupancy — for walk-ins booked = actual by definition (it
                 lives on the package), so only the statutory gender fallback
                 and infants are asked for here. ── */}
          <div style={{
            marginTop: '1rem', paddingTop: '0.75rem',
            borderTop: '1px solid var(--border-color)',
          }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
              Occupancy
            </div>
            {/* Fallback gender split — hidden once every named adult guest
                row carries a gender (server derives the counts then). */}
            {showGenderFallback && (
              <div className="form-grid-2" style={{ marginBottom: '0.75rem' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Adults — Male</label>
                  <input type="number" min={0} className="form-input" value={numMale}
                    onChange={(e) => setNumMale(e.target.value)} disabled={submitting} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Adults — Female</label>
                  <input type="number" min={0} className="form-input" value={numFemale}
                    onChange={(e) => setNumFemale(e.target.value)} disabled={submitting} />
                </div>
              </div>
            )}

            <div className="form-group" style={{ marginBottom: 0, maxWidth: '140px' }}>
              <label className="form-label">Infants</label>
              <input type="number" min={0} className="form-input" value={infants}
                onChange={(e) => setInfants(e.target.value)} disabled={submitting} />
            </div>
          </div>
        </>
      ),
    },

    // ── ③ GRC Details — same shape and order as Quick Check-In, so the desk
    //      learns one registration flow. Remarks folded in (Aug 2026
    //      redesign). Informational: none of these refuse a walk-in. ──
    {
      id: 'grc',
      title: 'GRC Details',
      badge: { done: grcFilled, total: 3 },
      errorCount: errorCounts['grc'],
      content: (
        <>
          {/* Mobile and email live on the guest rows — contact details
              belong to a person, not a stay. The card snapshots the
              primary guest's, derived server-side at save. */}
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={grcAddress}
              onChange={(e) => setGrcAddress(e.target.value)}
              rows={2}
              maxLength={250}
              placeholder="Permanent / residential address"
              style={{ resize: 'vertical', minHeight: '50px' }}
              disabled={submitting}
            />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Purpose of Visit</label>
              <input
                type="text"
                className="form-input"
                value={purposeOfVisit}
                onChange={(e) => setPurposeOfVisit(e.target.value)}
                maxLength={50}
                placeholder="Business / Leisure / Other"
                disabled={submitting}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Next Destination</label>
              <input
                type="text"
                className="form-input"
                value={nextDestination}
                onChange={(e) => setNextDestination(e.target.value)}
                maxLength={100}
                placeholder="Where the guest travels on to"
                disabled={submitting}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Remarks</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={200}
              placeholder="Optional — anything the next shift should know?"
              style={{ resize: 'vertical', minHeight: '50px' }}
              disabled={submitting}
            />
          </div>
        </>
      ),
    },

    // ── ④ Rate & Advance — the commercial fact plus the money taken. Comes
    //      BEFORE Rooms: allocation follows the package, never the other way
    //      round, and the advance-required estimate needs the priced total. ──
    {
      id: 'billing',
      title: 'Rate & Advance',
      badge: commercial ? (belowMin ? 'warn' : 'ok') : { done: 0, total: 1 },
      errorCount: errorCounts['billing'],
      content: (
        <>
          <WalkInBillingSection
            categories={availability?.categories ?? []}
            value={commercial}
            onChange={(v) => {
              ['package', 'commercialPax', 'rate', 'rateChangeReason', 'occupancyOverrideReason', 'rooms']
                .forEach(clearFieldError);
              setCommercial(v);
            }}
            numNights={Math.max(1, nights)}
            canEditRate={canEditRate}
            disabled={submitting}
            fieldErrors={fieldErrors}
          />
          {belowMin && commercial && (
            <div className="wi-warn" style={{ marginTop: '0.5rem' }}>
              Below the plan minimum — the booking will go for approval before check-in.
            </div>
          )}

          {/* Advance */}
          <div style={{
            padding: '0.5rem 0.65rem', borderRadius: '6px', marginTop: '0.75rem',
            border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: 0, color: 'var(--text-primary)' }}>Advance</h3>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                {advanceRequired > 0
                  ? <>required ₹{fmtMoney(advanceRequired)} ({advancePolicy?.advancePercent ?? 0}%)</>
                  : 'not required — confirms instantly'}
              </span>
            </div>
            <div className="form-grid-3">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Amount</label>
                <input type="number" min={0} className="form-input" value={advanceAmount}
                  onChange={(e) => { advanceTouchedRef.current = true; setAdvanceAmount(e.target.value); }}
                  disabled={submitting} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }} data-field="advanceMode">
                <SyutSelect
                  label="Mode"
                  options={PAYMENT_MODES.map((m) => ({ value: m, label: m }))}
                  value={advanceMode}
                  onChange={(v) => { modeTouchedRef.current = true; clearFieldError('advanceMode'); setAdvanceMode((v as string) || 'Cash'); }}
                  isClearable={false}
                  size="sm"
                  error={fieldErrors.advanceMode}
                  isDisabled={submitting || advAmtNum === 0}
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Reference</label>
                <input type="text" className="form-input" value={advanceRef} maxLength={50}
                  onChange={(e) => setAdvanceRef(e.target.value)}
                  placeholder="UPI ref / last-4…"
                  disabled={submitting || advAmtNum === 0} />
              </div>
            </div>
          </div>
        </>
      ),
    },

    // ── ⑤ Rooms — physical allocation against the package, full width. A
    //      chip in another category is an upgrade: hosted there, charged as
    //      booked. Last in the flow — the final act before submit. ──
    {
      id: 'rooms',
      title: 'Rooms',
      badge: commercial
        ? { done: selectedRooms.length, total: commercial.numberOfRooms }
        : undefined,
      errorCount: errorCounts['rooms'],
      content: (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {commercial
                ? `${selectedRooms.length} of ${commercial.numberOfRooms} allocated`
                : 'choose a rate package first'}
            </span>
            {loadingRooms && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Loading…</span>}
          </div>
          {fieldErrors.rooms && <div className="form-error-message" style={{ marginBottom: '0.4rem' }}>{fieldErrors.rooms}</div>}

          {/* The section pane itself scrolls — no nested scroll areas. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {(availability?.categories ?? []).map((cat) => {
              const freeRooms = roomsByCategory[cat.roomCategoryId] ?? [];
              const groupSelected = selectedRooms.filter((r) => r.roomCategoryId === cat.roomCategoryId);
              const isBookedCat = commercial?.roomCategoryId === cat.roomCategoryId;
              return (
                <div key={cat.roomCategoryId} style={{
                  border: `1px solid ${groupSelected.length ? 'rgba(37, 99, 235, 0.45)' : 'var(--border-color)'}`,
                  borderRadius: '6px', padding: '0.45rem 0.55rem',
                  background: groupSelected.length ? 'rgba(37, 99, 235, 0.04)' : 'transparent',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                    <strong style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{cat.categoryName}</strong>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {freeRooms.length} room{freeRooms.length !== 1 ? 's' : ''} free on {checkInDate || 'arrival'}
                      {cat.availableRooms < freeRooms.length ? ` · only ${Math.max(0, cat.availableRooms)} for ${nights}n` : ''}
                    </span>
                    {/* A room here sleeps in this category but stays booked and
                        charged as the package — the whole point of the split. */}
                    {commercial && !isBookedCat && groupSelected.length > 0 && (
                      <span style={{
                        marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700,
                        color: '#047857', background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.35)',
                        borderRadius: '9px', padding: '0.05rem 0.4rem', whiteSpace: 'nowrap',
                      }}>
                        ↑ upgrade — charged as {commercial.categoryName}
                      </span>
                    )}
                    {commercial && isBookedCat && (
                      <span style={{ marginLeft: 'auto', fontSize: '0.68rem', fontWeight: 700, color: 'var(--primary)' }}>
                        booked category
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {freeRooms.length === 0 && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>No free rooms.</span>
                    )}
                    {freeRooms.map((room) => {
                      const isSelected = selectedRooms.some((r) => r.roomId === room.roomId);
                      const isDirty = room.housekeepingStatusCode === 'ROOM_DIRTY';
                      return (
                        <button
                          key={room.roomId}
                          type="button"
                          onClick={() => toggleRoom(cat, room)}
                          disabled={submitting}
                          title={`${room.roomNumber}${room.floorNumber ? ` · Floor ${room.floorNumber}` : ''}${room.housekeepingStatusName ? ` · ${room.housekeepingStatusName}` : ''}`}
                          style={{
                            padding: '0.3rem 0.6rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 600,
                            cursor: 'pointer',
                            border: `1.5px solid ${isSelected ? '#2563eb' : isDirty ? 'rgba(245, 158, 11, 0.6)' : 'var(--form-border-color)'}`,
                            background: isSelected ? '#2563eb' : 'transparent',
                            color: isSelected ? '#fff' : 'var(--text-primary)',
                          }}
                        >
                          {room.roomNumber}{isDirty ? ' ⚠' : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Running total */}
          <div style={{
            marginTop: '0.6rem', padding: '0.5rem 0.65rem', borderRadius: '6px',
            border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.4rem',
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {selectedRooms.length} room{selectedRooms.length !== 1 ? 's' : ''} × {nights} night{nights !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              ₹{fmtMoney(total)}
              {advAmtNum > 0 && (
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                  adv ₹{fmtMoney(advAmtNum)} · bal ₹{fmtMoney(Math.max(0, total - advAmtNum))}
                </span>
              )}
            </span>
          </div>
        </>
      ),
    },
  ];

  const primaryGuestName = guestRows.find((g) => g.isPrimary)?.name.trim();

  return (
    <>
      <SectionedModalShell
        title={<>Walk-In — {checkInDate || today} · {nights} night{nights !== 1 ? 's' : ''}{relatedBookingNumber ? <> · adds rooms to {relatedBookingNumber}</> : null}</>}
        headerExtra={
          /* GRC number — same control and placement as Quick Check-In, because
             it identifies the physical document the guest signs and the desk
             should not have to look in two different places for it. Generated
             server-side by the check-in leg; the override exists for properties
             running a pre-printed GRC book. */
          <span className="chk-grc" title="Guest Registration Card number">
            GRC{' '}
            {grcNumberEditing ? (
              <input
                type="text"
                className="form-input"
                autoFocus
                value={grcNumber}
                onChange={(e) => setGrcNumber(e.target.value)}
                onBlur={() => setGrcNumberEditing(false)}
                maxLength={30}
                placeholder="Card number"
                disabled={submitting}
                style={{ width: '11rem', padding: '0.15rem 0.35rem', fontSize: '0.75rem' }}
              />
            ) : (
              <>
                <b>{grcNumber.trim() || 'auto-generated'}</b>
                <button
                  type="button"
                  onClick={() => setGrcNumberEditing(true)}
                  disabled={submitting}
                  title="Enter the number from a pre-printed GRC book"
                  style={{
                    marginLeft: '0.3rem', border: 'none', background: 'transparent',
                    color: 'var(--primary)', cursor: 'pointer', fontWeight: 700, padding: 0,
                  }}
                >
                  ✎
                </button>
              </>
            )}
          </span>
        }
        summary={
          <>
            <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>
              {primaryGuestName || 'New walk-in guest'}
            </strong>
            <span style={{ color: 'var(--text-secondary)' }}>
              {checkInDate || today} → {checkOutDate || '—'} · {nights} night{nights !== 1 ? 's' : ''}
            </span>
            {commercial && (
              <span style={{ color: 'var(--text-secondary)' }}>
                {commercial.categoryName} × {commercial.numberOfRooms}
              </span>
            )}
            {/* Live status chips — the decision-critical state stays visible
                whatever section is open. */}
            {commercial ? (
              <ShellChip
                tone={selectedRooms.length === commercial.numberOfRooms ? 'ok' : 'warn'}
                title="Rooms allocated against the package"
              >
                Rooms {selectedRooms.length}/{commercial.numberOfRooms}
              </ShellChip>
            ) : (
              <ShellChip tone="warn" title="The package prices the stay">No package yet</ShellChip>
            )}
            <ShellChip tone="info" title="Priced from the rate package">₹{fmtMoney(total)}</ShellChip>
            {advAmtNum > 0 && (
              <ShellChip tone="info" title="Advance being collected · balance after it">
                adv ₹{fmtMoney(advAmtNum)} · bal ₹{fmtMoney(Math.max(0, total - advAmtNum))}
              </ShellChip>
            )}
            {backdated && <ShellChip tone="warn">Backdated entry</ShellChip>}
          </>
        }
        sections={sections}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        onClose={onClose}
        onMinimize={tabId ? handleMinimize : undefined}
        busy={submitting}
        loading={{ active: submitting, label: 'Booking & checking in…' }}
        footer={
          <>
            {/* Say what is blocking instead of making the operator press the
                button to find out — each entry jumps to its section. */}
            <ShellBlockers blockers={blockerItems} onJump={setActiveSection} />
            <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || loadingRooms}>
              <IconSystem.check size={16} />
              {commercial
                ? `Check In Walk-In · ${commercial.numberOfRooms} room${commercial.numberOfRooms !== 1 ? 's' : ''} · ₹${fmtMoney(total)}`
                : 'Check In Walk-In'}
            </button>
          </>
        }
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Walk-In"
        message={confirmMessage}
        confirmLabel="Proceed"
        cancelLabel="Back"
        variant="default"
        onConfirm={() => { void executeWalkIn(); }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default WalkInModal;
